import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Tabs, Input, Form, Col, Row, Switch } from 'antd';
import Button from '../../../../common/components/Button';
import EditThresholdsModal from '../EditThresholdsModal';
import ApproachesResetModal from '../ApproachesResetModal';
import { mapDataToInitialValues } from '../../utils';
import { CHANNEL_TYPES } from '../../constants';
import { numericValidator } from '../../../../common/utils/validations';
import InfoTooltip from '../../../../common/components/InfoTooltip';
import {
  setIntersectionThresholds,
  setIntersectionSaved,
} from '../../store/slice';
import './style.css';
import useUser from '../../../userAuth/hooks/useUser';
import { UserRole } from '../../../../common/enums';

const { TabPane } = Tabs;

const ThresholdsForm = ({
  formData,
  channelNames,
  onFormDataChange,
  priority,
  resetToDefaults,
}) => {
  const [allChannelsData, setAllChannelsData] = useState([]);
  const [allChannelsClasses, setAllChannelsClasses] = useState([]);
  const [editing, setEditing] = useState(false);
  const [currentChannel, setCurrentChannel] = useState(0);
  const [showResetModal, setShowResetModal] = useState(false);

  const [form] = Form.useForm();

  const { userHasRole } = useUser();

  useEffect(() => {
    const channelsData = [];
    const channelsClasses = {};

    formData?.forEach((data, index) => {
      const { classes, ...channel } = data;
      channelsData[index] = channel;
      channelsClasses[index] = classes;
    });

    const initialValues = mapDataToInitialValues(channelsData);
    form.setFieldsValue(initialValues);
    setAllChannelsData(channelsData);
    setAllChannelsClasses(channelsClasses);
  }, [form, formData]);

  const handleFormDataChange = useCallback(
    (channelsClasses) => {
      const formValues = form.getFieldsValue();
      const channelsData = [...allChannelsData];

      // Form has maxCallTime and lostSignalHold for each channel, but the
      // data needs to be formatted in the manner expected by the API
      Object.entries(formValues)?.forEach((entry) => {
        const [key, value] = entry;
        const [index, output] = key.split('-');
        if (!(index in channelsData)) channelsData[index] = {};
        channelsData[index][output] = value;
      });

      channelsData?.forEach((channel, index) => {
        channel.classes = channelsClasses
          ? channelsClasses[index]
          : allChannelsClasses[index];
      });

      onFormDataChange(channelsData);
    },
    [form, allChannelsData, allChannelsClasses, onFormDataChange]
  );

  const applyToAll = useCallback(() => {
    const formValues = form.getFieldsValue();

    // Load channel 'A' settings
    const lostSignalHold = `${formValues['0-lostSignalHold']}`;
    const maxCallTime = `${formValues['0-maxCallTime']}`;
    const preemptEnabled = formValues['0-preemptEnabled'];
    if (lostSignalHold === undefined || maxCallTime === undefined) return;
    const channelClasses = allChannelsClasses[0];

    const updatedAllChannelsData = allChannelsData?.map((data, _) => {
      const channel = { ...data };
      channel.maxCallTime = maxCallTime;
      channel.lostSignalHold = lostSignalHold;
      channel.classes = channelClasses;
      channel.preemptEnabled = preemptEnabled;
      return channel;
    });

    onFormDataChange(updatedAllChannelsData);
  }, [allChannelsClasses, allChannelsData, form, onFormDataChange]);

  const handleClick = (index) => {
    setCurrentChannel(index);
    setEditing(true);
  };

  const isEditor = userHasRole([UserRole.ORG_ADMIN, UserRole.READ_WRITE]);

  return (
    <>
      <Form
        form={form}
        className="thresholds-form"
        layout="vertical"
        onFieldsChange={() => handleFormDataChange()}
      >
        <Row>
          {formData?.map((_, index) => (
            <Col key={index} span={6}>
              <p className="thresholds-form-channel-title">
                Channel {CHANNEL_TYPES[index]}
                {` `}
                {channelNames[index] !== '' && `(${channelNames[index]})`}
              </p>
            </Col>
          ))}
        </Row>
        <Row>
          {formData?.map((_, index) => (
            <Col key={index} span={6}>
              <Form.Item
                className="priority-switch"
                name={`${index}-preemptEnabled`}
                label={<p>Priority Enabled</p>}
                valuePropName="checked"
              >
                <Switch disabled={!isEditor} />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Row>
          {formData?.map((_, index) => (
            <Col key={index} span={6}>
              <Form.Item
                className="thresholds-form-item"
                name={`${index}-maxCallTime`}
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Max Call Time (seconds)
                    <InfoTooltip
                      text={
                        <>
                          This setting specifies the maximum time that an
                          individual call is allowed to be continuously held
                          active. There are separate settings for low and high
                          priority calls and for each channel. After the time
                          expires, the call will be considered
                          &quot;overruled&quot; for that particular vehicle ID.
                          Other vehicles approaching the intersection from the
                          same direction &#40;with different vehicle IDs&#41;
                          can still place calls on the channel. <br />
                          Default: 360 <br />
                          Min/Max: Min 60, Max 65535
                        </>
                      }
                    />
                  </div>
                }
                rules={[
                  numericValidator({
                    min: 0,
                    max: 65535,
                    errorMessage:
                      'Max Call Times should be within 0 - 65535 seconds',
                  }),
                ]}
              >
                <Input disabled={!isEditor} />
              </Form.Item>
              <Form.Item
                className="thresholds-form-item"
                name={`${index}-lostSignalHold`}
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Lost Signal Hold (seconds)
                    <InfoTooltip
                      text={
                        <>
                          This setting specifies the amount of time that an
                          individual call will continue to be placed after the
                          phase selector no longer detects the vehicle. This
                          time will apply if the vehicle equipment is shut off,
                          if an obstruction has blocked radio/infrared
                          reception, or the radio signal strength (GPS) drops to
                          a point that communication becomes intermittent.{' '}
                          <br />
                          Default: 6 <br />
                          Min/Max: Min 1, Max 255
                        </>
                      }
                    />
                  </div>
                }
                rules={[
                  numericValidator({
                    min: 0,
                    max: 65535,
                    errorMessage:
                      'Lost Signal Hold should be within 0 - 65535 seconds',
                  }),
                ]}
              >
                <Input disabled={!isEditor} />
              </Form.Item>
              <Form.Item
                className="thresholds-form-item"
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    ETA & Distance Thresholds
                    <InfoTooltip
                      text={
                        <>
                          Estimated Time of Arrival &#40;ETA&#41; and Distance
                          are two parameters that are used together to determine
                          when to submit a priority request to the traffic
                          controller. Priority requests will be submitted to the
                          controller as soon as either of the thresholds are
                          reached. The ETA Threshold is measured in seconds and
                          will likely be the value used to determine when the
                          phase selector&apos;s output is activated for fast
                          moving approaching vehicles. The Distance Threshold is
                          measured in feet and will likely be the value used to
                          determine when the phase selector&apos;s output is
                          activated for slow moving approaching vehicles. There
                          are separate settings for low and high priority calls,
                          for each channel, and for each vehicle class. An
                          approaching vehicle also needs to be in an approach.
                          If an approach is drawn too short, it will not matter
                          what the ETA or Distance Thresholds are.
                          <br /> Default ETA: 30
                          <br /> Min/Max: 0-255
                          <br /> Default Distance: 1000
                          <br /> Min/Max: 0-10000
                        </>
                      }
                    />
                  </div>
                }
              >
                <Button
                  className="thresholds-button-edit"
                  onClick={() => handleClick(index)}
                >
                  Thresholds
                </Button>
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Row>
          <Col span={15}>
            <div className="thresholds-buttons">
              <Button
                className="thresholds-button-apply"
                type="secondary"
                size="lg"
                onClick={() => applyToAll()}
                hidden={!isEditor}
              >
                Apply Channel {`<A>`} settings to all Channels (?)
              </Button>
              <Button
                type="danger"
                size="lg"
                onClick={() => setShowResetModal(true)}
                hidden={!isEditor}
              >
                Reset Threshold Defaults
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
      <EditThresholdsModal
        showModal={editing}
        setShowModal={setEditing}
        channel={currentChannel}
        allChannelsClasses={allChannelsClasses}
        onAllChannelsClassesChange={(channels) =>
          handleFormDataChange(channels)
        }
        priorityLevel={priority}
      />
      <ApproachesResetModal
        visible={showResetModal}
        onSubmit={() => {
          resetToDefaults(priority);
          setShowResetModal(false);
        }}
        onCancel={() => setShowResetModal(false)}
        type="thresholds"
      />
    </>
  );
};

const ApproachesThresholds = ({
  thresholds,
  channelNames,
  resetToDefaults,
}) => {
  const { lowPriority, highPriority } = thresholds;

  const dispatch = useDispatch();

  const handleLowPriorityThresholdsChange = useCallback(
    (t) => {
      dispatch(setIntersectionThresholds({ ...thresholds, lowPriority: t }));
      dispatch(setIntersectionSaved(false));
    },
    [dispatch, thresholds]
  );

  const handleHighPriorityThresholdsChange = useCallback(
    (t) => {
      dispatch(setIntersectionThresholds({ ...thresholds, highPriority: t }));
      dispatch(setIntersectionSaved(false));
    },
    [dispatch, thresholds]
  );

  return (
    <Tabs
      className="thresholds-tabs"
      tabBarStyle={{ backgroundColor: '#f1efef' }}
    >
      <TabPane tab="Low Priority" key="2.1">
        <ThresholdsForm
          formData={lowPriority}
          channelNames={channelNames}
          onFormDataChange={handleLowPriorityThresholdsChange}
          priority={'Low'}
          resetToDefaults={resetToDefaults}
        />
      </TabPane>
      <TabPane tab="High Priority" key="2.2">
        <ThresholdsForm
          formData={highPriority}
          channelNames={channelNames}
          onFormDataChange={handleHighPriorityThresholdsChange}
          priority={'High'}
          resetToDefaults={resetToDefaults}
        />
      </TabPane>
    </Tabs>
  );
};

export default ApproachesThresholds;
